import React, { useRef, useEffect } from "react";

import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Button,
} from "@mui/material";

import { useReactToPrint } from "react-to-print";
import { useNavigate, useLocation } from "react-router";
import dayjs from "dayjs";
import numberToWords from "number-to-words"; // Make sure this import is actually used if needed for amount in words
import QrCodeGenerator from "views/commonComponents/QrCode/QrCodeGenerator";

const parseInvoiceData = () => {
  const companyInfo = {
    name: "مؤسسة غيوم الجود للتجارة", // AL JAZIRAH DIST.
    address: "ESMAT IBN AL HUSAYN", //
    phone: "Ph:", //
    gstNo: "311372128400003", //
  };

  const invoiceDetails = {
    invoiceNo: "3", //
    date: "16-06-2025", //
    time: "11:53 AM", //
    partyName: "Cash", //
    address: "NIL", //
    phone: "0", //
    vatNo: "0", //
  };

  const items = [
    { name: "BALL", qty: 6.0, rate: 20.0, vatAmt: 0.0, netAmt: 120.0 }, //
    {
      name: "BROAST CHICKEN",
      qty: 3.0,
      rate: 300.0,
      vatAmt: 0.0,
      netAmt: 900.0,
    }, //
    { name: "KUBOOS", qty: 6.0, rate: 10.0, vatAmt: 2.86, netAmt: 60.0 }, //
  ];

  const summary = {
    netQty: 15.0, //
    quickTotal: 1080.0, //
    totalBeforeTax: 1077.14, //
    vat: 2.86, //
    totalAmount: 1080.0, //
    discount: 0.0, //
    netTotal: 1080.0, //
  };

  const footer = {
    disclaimer: "تصريح مرتجع البقاعة الصلاحية قبل يوم شكرا لكم ..", //
    receivedByLabel: "Received By الستلم", //
    authorisedSignatoryLabel: "Authorised Signatory التوقيع", //
  };

  return { companyInfo, invoiceDetails, items, summary, footer };
};

const ThermalInch = () => {
  // Destructure invoiceData once, and use passed props if available
  const location = useLocation();
  // Using default empty objects for data and tableData to prevent errors if state is undefined
  const { data = {}, tableData = [], qrData } = location.state || {};

  const componentRef = useRef();
  const company = useSelector((state) => state.common.companyInfo);
  const navigate = useNavigate();
  const decimalCount = useSelector((state) => state.common.decimalCount);
  const primaryCurrency = useSelector((state) => state.common.primaryCurrency);

  const { footer } = parseInvoiceData();
  const { companyInfo, items } = parseInvoiceData();

  const calculatedNetQty = tableData.length;

  // Fallback for company information if not from Redux or props
  const displayCompany = company || companyInfo;

  // Use tableData if available, otherwise use mock items
  const displayItems = tableData.length > 0 ? tableData : items;

  // Handle numberToWords usage (assuming you want to convert the net total)
  const amountInWords = numberToWords.toWords(
    Math.floor(data.BillNetTotalField)
  );
  const decimalPart = Math.round(
    (data.BillNetTotalField - Math.floor(data.BillNetTotalField)) * 100
  );
  const decimalWords =
    decimalPart > 0 ? ` and ${numberToWords.toWords(decimalPart)} fils` : "";

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      // This will be called after the printing is completed
      navigate(-2);
    },
    pageStyle: `
      @page {
       
        margin: 0;
      }
      `,
  });

  useEffect(() => {
    // Only call handlePrint if company is loaded, or if data is present
    if (displayCompany && (data || tableData)) {
      handlePrint();
    }
  }, [displayCompany, data, tableData, handlePrint]); // Added handlePrint to dependencies

  // Basic check to ensure essential data exists before rendering
  if (!data && !tableData.length) {
    return (
      <Typography
        variant="h6"
        color="error"
        sx={{ textAlign: "center", mt: 4 }}
      >
        No invoice data to display. Please ensure data is passed via location
        state.
      </Typography>
    );
  }

  return (
    <>
      <Button onClick={() => navigate(-2)}>Back</Button>
      <Button onClick={() => handlePrint()}>Print</Button>
      <Paper
        ref={componentRef}
        sx={{
          // maxWidth: 384,
          margin: "auto",
          mt: 4,
          mb: 4,
          fontFamily: "Arial, sans-serif",
          fontSize: "0.85rem",
          p: 0.2,
        }}
      >
        <Box sx={{ border: "1px solid black", p: 0.2 }}>
          {/* Company Header */}
          <Box sx={{ textAlign: "center", p: 0.5 }}>
            {/* QR Code can be placed here if you want it at the top */}
            <Typography
              variant="h5"
              component="h1"
              fontWeight="bold"
              sx={{ fontSize: "1.2rem", mb: 0.5 }}
            >
              {displayCompany.Name}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
              {displayCompany.Add1}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
              {displayCompany.Ph}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
              GST No: {displayCompany.Add2}
            </Typography>
            <Typography
              variant="h6"
              sx={{ mt: 2, mb: 1, fontSize: "0.8rem", fontWeight: "bold" }}
            >
              فاتورة ضريبية / TAX INVOICE
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5, borderColor: "grey.400" }} />

          {/* Invoice Details */}
          <Grid container spacing={0.5} sx={{ p: 1 }}>
            <Grid item xs={3.5}>
              {" "}
              {/* Adjusted width */}
              <Typography
                variant="body2"
                sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
              >
                <strong>Invoice No </strong>
              </Typography>
            </Grid>
            <Grid item xs={8.5}>
              {" "}
              {/* Adjusted width */}
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                : {data.BillNo}
              </Typography>
            </Grid>
            <Grid item xs={3.5}>
              {" "}
              {/* Adjusted width */}
              <Typography
                variant="body2"
                sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
              >
                <strong>Date</strong>
              </Typography>
            </Grid>
            <Grid item xs={8.5}>
              {" "}
              {/* Adjusted width */}
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                : {dayjs(data.BillDate).format("DD/MM/YYYY")}
              </Typography>
            </Grid>
            <Grid item xs={3.5}>
              {" "}
              {/* Adjusted width */}
              <Typography
                variant="body2"
                sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
              >
                <strong>Party Name</strong>
              </Typography>
            </Grid>
            <Grid item xs={8.5}>
              {" "}
              {/* Adjusted width */}
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                : {data.BillPartyName}
              </Typography>
            </Grid>
            <Grid item xs={3.5}>
              {" "}
              {/* Adjusted width */}
              <Typography
                variant="body2"
                sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
              >
                <strong>Address</strong>
              </Typography>
            </Grid>
            <Grid item xs={8.5}>
              {" "}
              {/* Adjusted width */}
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                :{data.Address1}
              </Typography>
            </Grid>
            <Grid item xs={3.5}>
              {" "}
              {/* Adjusted width */}
              <Typography
                variant="body2"
                sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
              >
                <strong>Ph</strong>
              </Typography>
            </Grid>
            <Grid item xs={8.5}>
              {" "}
              {/* Adjusted width */}
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                :{data.Ph}
              </Typography>
            </Grid>
            <Grid item xs={3.5}>
              {" "}
              {/* Adjusted width */}
              <Typography
                variant="body2"
                sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
              >
                <strong>Vat No</strong>
              </Typography>
            </Grid>
            <Grid item xs={8.5}>
              {" "}
              {/* Adjusted width */}
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                : {data.TinNo}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 0.5, borderColor: "grey.400" }} />

          {/* Items Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      p: 0,
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      p: 0,
                    }}
                  >
                    Item Name
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      p: 0,
                    }}
                  >
                    Qty
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      p: 0,
                    }}
                  >
                    Rate
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      p: 0,
                    }}
                  >
                    Tax Amt
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      p: 0,
                    }}
                  >
                    Net Amt
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayItems.map((item, index) => (
                  <React.Fragment key={index}>
                    {/* First row: Serial No. and Item Name (NO BORDERS) */}
                    <TableRow sx={{ "& > td": { border: "none" } }}>
                      <TableCell sx={{ fontSize: "0.8rem", p: 0, px: 0.2 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell colSpan={5} sx={{ fontSize: "0.8rem", p: 0 }}>
                        {item.ItemNameTextField || item.name}
                      </TableCell>
                    </TableRow>

                    {/* Second row: Qty, Rate, Tax Amt, Net Amt (WITH BORDERS) */}
                    <TableRow>
                      <TableCell
                        colSpan={2} // Span 2 columns to align with item name, but no content
                        sx={{
                          fontSize: "0.8rem",
                          p: 0,
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {/* Empty cells to align data */}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "0.8rem",
                          p: 0,
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {item.qty?.toFixed(decimalCount) || "0.00"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "0.8rem",
                          p: 0,
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {item.Rate1?.toFixed(decimalCount) ||
                          item.rate?.toFixed(decimalCount) ||
                          "0.00"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "0.8rem",
                          p: 0,
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {item.taxAmt?.toFixed(decimalCount) ||
                          item.vatAmt?.toFixed(decimalCount) ||
                          "0.00"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "0.8rem",
                          p: 0,
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {item.total?.toFixed(decimalCount) ||
                          item.netAmt?.toFixed(decimalCount) ||
                          "0.00"}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 0.5, borderColor: "grey.400" }} />

          {/* Net Qty and Quick Total Section - Adjusted Position */}
          <Grid container spacing={0.5} sx={{ px: 0.5 }}>
            <Grid
              item
              xs={6}
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "baseline",
              }}
            >
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ fontSize: "0.8rem", mr: 0.5 }}
              >
                Net Qty:
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                {calculatedNetQty.toFixed(decimalCount)}
              </Typography>
            </Grid>

            <Grid
              item
              xs={6}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "baseline",
              }}
            >
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "0.8rem", mr: 0.5 }}
              >
                Total:
              </Typography>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "0.8rem" }}
              >
                {data.BillNetTotalField.toFixed(decimalCount)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 0.5, borderColor: "grey.400" }} />

          {/* Detailed Summary */}
          <Box>
            <Grid container spacing={0.5} sx={{ px: 0.5 }}>
              {/* Row for TOTAL BEFORE TAX */}
              <Grid
                item
                xs={8} // This grid item defines the space for the label
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start", // Left align the content within this Grid item
                }}
              >
                {/* Label - First Line */}
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    fontSize: "0.8rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  TOTAL BEFORE TAX / الإجمالي
                </Typography>
                {/* Label - Second Line, if needed */}
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    fontSize: "0.8rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  بدون ضريبة
                </Typography>
              </Grid>
              <Grid
                item
                xs={0.5} // Dedicated Grid item for the colon
                sx={{
                  display: "flex",
                  alignItems: "baseline", // Aligns with the baseline of the first line of the label
                  justifyContent: "center", // Center the colon horizontally in its small column
                }}
              >
                {/* Colon for TOTAL BEFORE TAX */}
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                  :
                </Typography>
              </Grid>
              <Grid
                item
                xs={3.5} // Dedicated Grid item for the value
                sx={{
                  display: "flex",
                  justifyContent: "flex-end", // Push value to the right
                  alignItems: "baseline", // Aligns with the baseline of the first line of the label
                }}
              >
                {/* Value for TOTAL BEFORE TAX */}
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                  {(data.BillNetTotalField - data.TaxAmt).toFixed(decimalCount)}
                </Typography>
              </Grid>

              {/* Row for VAT (15%) */}
              <Grid
                item
                xs={8}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start", // Left align
                }}
              >
                {/* Label - First Line with (15%) */}
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    fontSize: "0.8rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  ضريبة القيمة / (15%) Vat
                </Typography>
                {/* Label - Second Line */}
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    fontSize: "0.8rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  المضافة
                </Typography>
              </Grid>
              <Grid
                item
                xs={0.5}
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                }}
              >
                {/* Colon for VAT */}
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                  :
                </Typography>
              </Grid>
              <Grid
                item
                xs={3.5}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "baseline",
                }}
              >
                {/* Value for VAT */}
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                  {(data.TItTaxAmt - data.TaxAmt).toFixed(decimalCount)}
                </Typography>
              </Grid>

              {/* Row for Total Amt (Single line) */}
              <Grid
                item
                xs={8}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start", // Left align
                  alignItems: "baseline",
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
                >
                  Total Amt / المجموع
                </Typography>
              </Grid>
              <Grid
                item
                xs={0.5}
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
                  :
                </Typography>
              </Grid>
              <Grid
                item
                xs={3.5}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "baseline",
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ fontSize: "0.8rem" }}
                >
                  {data.BillTotalField.toFixed(decimalCount)}
                </Typography>
              </Grid>

              {/* Row for Discount (Single line) */}
              <Grid
                item
                xs={8}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start", // Left align
                  alignItems: "baseline",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
                >
                  Discount / خصم
                </Typography>
              </Grid>
              <Grid
                item
                xs={0.5}
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                  :
                </Typography>
              </Grid>
              <Grid
                item
                xs={3.5}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "baseline",
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                  {/* {displayDiscount.toFixed(decimalCount)} */}
                  {data.BillDiscAmtField}
                </Typography>
              </Grid>

              {/* Row for NET TOTAL (Single line) */}
              <Grid
                item
                xs={8}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start", // Left align
                  alignItems: "baseline",
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
                >
                  NET TOTAL / مجموع الصافي
                </Typography>
              </Grid>
              <Grid
                item
                xs={0.5}
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
                  :
                </Typography>
              </Grid>
              <Grid
                item
                xs={3.5}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "baseline",
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ fontSize: "0.9rem" }}
                >
                  {data.BillNetTotalField.toFixed(decimalCount)}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Amount in words */}
          <Box
            sx={{ p: 1, textAlign: "left", borderBottom: "1px dashed #ccc" }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
              {primaryCurrency} In words: {amountInWords} {decimalWords} only.
            </Typography>
          </Box>

          <Divider sx={{ my: 1, borderColor: "grey.400" }} />

          <Box sx={{ width: "100px", height: "100px", margin: "0 auto 10px" }}>
            <Box width={100}>
              {/* Ensure qrData is properly formatted for the QrCodeGenerator */}
              <QrCodeGenerator
                qrData={data.zatca_einv_qr_code || qrData || "No QR Data"}
              />
            </Box>
          </Box>
          {/* Footer / Disclaimer */}
          <Box sx={{ mt: 2, textAlign: "center", p: 1 }}>
            <Typography variant="body2" sx={{ mb: 2, fontSize: "0.8rem" }}>
              {footer.disclaimer}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} textAlign="center">
                <Typography
                  variant="body2"
                  sx={{
                    borderTop: "1px solid #333",
                    pt: 0.5,
                    mt: 3,
                    display: "inline-block",
                    minWidth: "150px",
                    fontSize: "0.8rem",
                  }}
                ></Typography>
              </Grid>
              <Grid item xs={6} textAlign="center">
                <Typography
                  variant="body2"
                  sx={{
                    borderTop: "1px solid #333",
                    pt: 0.5,
                    mt: 3,
                    display: "inline-block",
                    minWidth: "150px",
                    fontSize: "0.8rem",
                  }}
                ></Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default ThermalInch;
